<script setup>
  import { computed, ref, defineExpose } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { router } from '../router/index.js';
  import axios from '../services/ApiService.js';
  import Pagination from './Pagination.vue';
  import { BSpinner } from 'bootstrap-vue-next';

  const emits = defineEmits(['toggleDeleteModal', 'toggleUserModal', 'toggleAccessesModal', 'toggleRemoveAccessModal']);
  const { t } = useI18n();
  const i18nScope = ref('components.users_management');

  const props = defineProps({
    users: {
      type: Array,
      required: true
    },
    userType: {
      type: String,
      default: 'user'
    },
    selectedRole: {
      type: String,
      required: true,
      default: ''
    },
    selectedLibraryId: {
      type: Number,
      default: null
    },
    selectedRetailerId: {
      type: Number,
      default: null
    },
    selectedNetworkId: {
      // Number for a real network id, or the string 'none' ("Sans réseau" --
      // libraries/retailers with no network, FBKS-2843).
      type: [Number, String],
      default: null
    },
    searchQuery: {
      type: String,
      default: ''
    },
    differentiateUserVsRoleDeletion: {
      type: Boolean,
      default: false
    },
    // FBKS-4003: a library/network/retailer matching the role is selected.
    canRemoveAccess: {
      type: Boolean,
      default: false
    },
    currentUserId: {
      type: Number,
      default: null
    }
  });

  // const router = useRouter();
  const usersLoading = ref(false);
  const users = ref([]);
  const currentPage = ref(1);
  const itemsPerPage = ref(10);
  const maxPagesToShow = ref(12);
  const totalResults = ref(0);
  const columnsEnabled = ref({
    manageNetworks: false,
    manageLibraries: false,
    manageRetailers: false,
    manageCuration: false,
    manageAccount: false,
    manageActivateCarts: false,
    manageLibraryAdmins: false,
    manageRetailerAdmins: false,
    manageOrders: false
  });
  const selectedUser = ref({
    id: null,
    username: '',
    firstName: '',
    lastName: '',
    mail: ''
  });

  const handlePageChange = (page) => {
    currentPage.value = page;
    fetchUsers();
  };

  const calculateColumnsEnabled = () => {
    columnsEnabled.value.manageNetworks = false;
    columnsEnabled.value.manageLibraries = false;
    columnsEnabled.value.manageRetailers = false;
    columnsEnabled.value.manageCuration = false;
    columnsEnabled.value.manageAccount = false;
    columnsEnabled.value.manageActivateCarts = false;
    columnsEnabled.value.manageLibraryAdmins = false;
    columnsEnabled.value.manageRetailerAdmins = false;
    columnsEnabled.value.manageOrders = false;

    if (props.selectedRole == 'platform_manager') {
      columnsEnabled.value.manageNetworks = true;
      columnsEnabled.value.manageLibraries = true;
      columnsEnabled.value.manageRetailers = true;
      columnsEnabled.value.manageCuration = true;
    }

    // For Library Managers
    if (props.selectedRole == 'library_manager') {
      columnsEnabled.value.manageLibraryAdmins = true;
      columnsEnabled.value.manageActivateCarts = true;
    }

    // For Retailer Managers
    if (props.selectedRole == 'retailer_manager') {
      columnsEnabled.value.manageRetailerAdmins = true;
      columnsEnabled.value.manageOrders = true;
    }
  };

  const deleteModal = (user) => {
    setSelectedUser(user);
    emits('toggleDeleteModal', user);
  }

  const removeAccessModal = (user) => {
    setSelectedUser(user);
    emits('toggleRemoveAccessModal', user);
  }

  // FBKS-4003: translation key explaining why the access can't be removed, or null.
  // Only staff may unlink staff (the backend enforces it too).
  const removeAccessBlocker = (user) => {
    if (!props.canRemoveAccess) return 'remove_access_select_org';
    if (user.id === props.currentUserId) return 'remove_access_own';
    if (props.userType !== 'admin' && (user.admin || user.support || user.finance)) return 'remove_access_staff';

    return null;
  };

  const editUser = (user) => {
    setSelectedUser(user);
    emits('toggleUserModal', user);
  }

  const viewAccesses = (user) => {
    setSelectedUser(user);
    emits('toggleAccessesModal', user);
  }

  const fetchUsers = async (reset = false) => {
    if (reset) clearUsers();
    users.value = [];
    totalResults.value = 0;
    usersLoading.value = true;
    let response = null;
    let url = `/api/users/list.json?page=${currentPage.value}&per_page=${itemsPerPage.value}&role=${props.selectedRole}`;
    if (props.selectedNetworkId != null) url += `&network_id=${props.selectedNetworkId}`;
    if (props.selectedLibraryId != null) url += `&library_id=${props.selectedLibraryId}`;
    if (props.selectedRetailerId != null) url += `&retailer_id=${props.selectedRetailerId}`;
    if (props.searchQuery) url += `&query=${encodeURIComponent(props.searchQuery)}`;

    try {
      response = await axios.get(url);
    } catch (error) {
      alert('Error fetching users');
      totalResults.value = 0;
      users.value = [];
      usersLoading.value = false;
      return;
    }

    totalResults.value = response.data?.total ? response.data.total : 0;
    users.value = response.data?.users ? response.data.users : [];
    calculateColumnsEnabled();
    usersLoading.value = false;
  }

  const clearUsers = () => {
    users.value = [];
    totalResults.value = 0;
    currentPage.value = 1;
    usersLoading.value = false;
  };

  defineExpose({
    clearUsers,
    fetchUsers
  });

  const showCheck = (type) => {
    return type === true ? 'bi bi-check-circle-fill icon_green' : 'bi bi-x-circle-fill icon_red';
  };

  const setSelectedUser = (user) => {
    if (user == null) {
      selectedUser.value = {};
      return;
    }
    selectedUser.value = {
      id: user.id,
      username: user.username,
      firstName: user.first_name,
      lastName: user.last_name,
      role: user.role,
      mail: user.email
    };
  }

  const impersonateUser = (user) => {
    // FBKS-2843: always redirect to `/` after impersonating -- the impersonated
    // user may not have access to /admin/users_management, and a 403 there
    // looped via request.referrer into a rate-limit error.
    router.push(`/?user_override=${user.id}`).then(() => {
      window.location.reload();
    });
  };

</script>

<template>
  <div v-if="!usersLoading && users.length > 0" class="my-4">
    <table class="table table-hover">
      <thead>
        <tr>
          <th scope="col">{{ t(`${i18nScope}.columns.username`) }}</th>
          <th scope="col">{{ t(`${i18nScope}.columns.email`) }}</th>
          <th scope="col" class="text-center" v-if="columnsEnabled.manageNetworks">
            {{ t(`${i18nScope}.columns.manage_networks`) }}
          </th>
          <th scope="col" class="text-center" v-if="columnsEnabled.manageLibraries">
            {{ t(`${i18nScope}.columns.manage_libraries`) }}
          </th>
          <th scope="col" class="text-center" v-if="columnsEnabled.manageRetailers">
            {{ t(`${i18nScope}.columns.manage_retailers`) }}
          </th>
          <th scope="col" class="text-center" v-if="columnsEnabled.manageCuration">
            {{ t(`${i18nScope}.columns.manage_curation`) }}
          </th>
          <th scope="col" class="text-center" v-if="columnsEnabled.manageAccount">
            {{ t(`${i18nScope}.columns.manage_account`) }}
          </th>
          <th scope="col" class="text-center" v-if="columnsEnabled.manageActivateCarts">
            {{ t(`${i18nScope}.columns.activate_purchase`) }}
          </th>
          <th scope="col" class="text-center" v-if="columnsEnabled.manageRetailerAdmins">
            {{ t(`${i18nScope}.columns.manage_retailer_admins`) }}
          </th>
          <th scope="col" class="text-center" v-if="columnsEnabled.manageLibraryAdmins">
            {{ t(`${i18nScope}.columns.manage_library_admins`) }}
          </th>
          <th scope="col" class="text-center" v-if="columnsEnabled.manageOrders">
            {{ t(`${i18nScope}.columns.manage_orders`) }}
          </th>
          <th scope="col">&nbsp;</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for='user in users' :key='user.id'>
          <td class="align-middle">
            {{user.fullName}}
            <br>
            <small>{{user.username}}</small>
          </td>

          <td class="align-middle">
            {{user.email}}
          </td>

          <td class="text-center align-middle" v-if="columnsEnabled.manageNetworks">
            <i :class="showCheck(user.manageNetworks)"></i>
          </td>
          <td class="text-center align-middle" v-if="columnsEnabled.manageLibraries">
            <i :class="showCheck(user.manageLibraries)"></i>
          </td>
          <td class="text-center align-middle" v-if="columnsEnabled.manageRetailers">
            <i :class="showCheck(user.manageRetailers)"></i>
          </td>
          <td class="text-center align-middle" v-if="columnsEnabled.manageCuration">
            <i :class="showCheck(user.manageCuration)"></i>
          </td>
          <td class="text-center align-middle" v-if="columnsEnabled.manageAccount">
            <i :class="showCheck(user.manageAccount)"></i>
          </td>
          <td class="text-center align-middle" v-if="columnsEnabled.manageActivateCarts">
            <i :class="showCheck(user.manageActivateCarts)"></i>
          </td>
          <td class="text-center align-middle" v-if="columnsEnabled.manageLibraryAdmins">
            <i :class="showCheck(user.manageLibraryAdmins)"></i>
          </td>
          <td class="text-center align-middle" v-if="columnsEnabled.manageRetailerAdmins">
            <i :class="showCheck(user.manageRetailerAdmins)"></i>
          </td>
          <td class="text-center align-middle" v-if="columnsEnabled.manageOrders">
            <i :class="showCheck(user.manageOrders)"></i>
          </td>

          <td class="align-end">
            <div class="btn-group form-btn-group float-end" role="group">
              <button
                type="button"
                v-if="user.can_impersonate"
                class="btn btn-dark align-bottom"
                @click="impersonateUser(user)"
              >
                <i class="bi bi-person-badge"></i>
                {{ t(`${i18nScope}.columns.impersonate`) }}
              </button>
              <button type="button" v-if="props.userType === 'admin'" class="btn btn-secondary align-bottom" :title="t(`${i18nScope}.accesses.view`)" v-on:click="viewAccesses(user)">
                <i class="bi bi-diagram-3"></i>
              </button>
              <button type="button" class="btn btn-primary align-bottom" :title="t(`${i18nScope}.columns.edit`)" v-on:click="editUser(user)">
                <i class="bi bi-pen"></i>
              </button>
              <!-- FBKS-4003: removes the access to the selected organisation, not the account.
                   The span keeps the tooltip visible while the button is disabled. -->
              <span
                v-if="props.differentiateUserVsRoleDeletion"
                class="btn-group"
                :title="t(`${i18nScope}.${removeAccessBlocker(user) || 'remove_access'}`)"
              >
                <button
                  type="button"
                  :class="['btn', 'align-bottom', removeAccessBlocker(user) ? 'btn-secondary' : 'btn-danger']"
                  :disabled="!!removeAccessBlocker(user)"
                  v-on:click="removeAccessModal(user)"
                >
                  <i class="bi bi-person-dash"></i>
                </button>
              </span>
              <button v-else type="button" class="btn btn-danger align-bottom" :title="t(`${i18nScope}.columns.delete`)" v-on:click="deleteModal(user)">
                <i class="bi bi-trash"></i>
              </button>
            </div>
          </td>
        </tr>
      </tbody>
    </table>

    <hr>

    <Pagination
      :currentPage="currentPage"
      :totalItems="totalResults"
      :itemsPerPage="itemsPerPage"
      :maxPagesToShow="maxPagesToShow"
      @page-change="handlePageChange"
      class="mt-4"
    />

  </div>

  <div class="d-flex justify-content-center align-items-center mt-3">
    <BSpinner style="width: 3rem; height: 3rem;" variant="secondary" v-if="usersLoading" />
    <div v-if="!usersLoading && users.length == 0">
      {{ t(`${i18nScope}.columns.no_users_found`) }}
    </div>
  </div>
</template>

<style scoped lang="scss">
.page-item {
  display: inline-block;
}

.page-item.disabled .page-link {
  color: #6c757d;
  pointer-events: none;
  background-color: #fff;
  border-color: #dee2e6;
}
</style>

<i18n src="../locales/en.json" global></i18n>
<i18n src="../locales/fr.json" global></i18n>
<i18n src="../locales/es.json" global></i18n>
<i18n src="../locales/it.json" global></i18n>
<i18n src="../locales/pt.json" global></i18n>
<i18n src="../locales/de.json" global></i18n>
